import {
  classifyPrimaryDesignation,
  judgePrimaryDesignation,
  PRIMARY_DESIGNATION_DENIALS,
  type PrimaryDesignationDenialReason as SharedDenialReason,
} from '@safetyhub/shared';
import type { AssignmentType, SiteAssignment } from './organizations';

/**
 * 정 담당자(PRIMARY) 지정·교체·강등의 **화면 어댑터** — 결정 기록
 * `docs/decisions/2026-09-26-primary-assignee-designation-wall.md`.
 *
 * ## ⚠️ 판정은 여기 없다 — `@safetyhub/shared`의 `utils/site-primary-designation.ts`가 소유한다
 *
 * 2026-09-27까지 이 파일에 진리표 **사본**이 있었고(서버 `sites/primary-designation-wall.ts`와 두 벌),
 * 짝지어진 검사는 **사유 문구뿐**이라 판정 동치는 아무도 재지 않았다. 그 틈으로 실제 결함이 들어왔다 —
 * ③(이것은 강등인가)의 축이 서버는 요청 타입, 화면은 대상 행 타입이었다(`AssignmentType`이 두 값이라 답이
 * 같아 사용자 영향은 0이었다). **사본은 문구보다 «축»에서 먼저 갈라지고, 문구 동치 spec은 그것을 원리적으로
 * 못 본다.** 그래서 분류와 진리표를 둘 다 shared로 올렸고 이 파일에는 **화면 전용 축**만 남았다:
 *
 * | 남은 것 | 왜 화면 전용인가 |
 * |---|---|
 * | `canRenderAssignmentTypeChoice` | 버튼 **자리**를 낼지 — 서버에는 그런 질문이 없다(가드가 막는다) |
 * | `NO_ASSIGN_PERMISSION` 사유·문구 | 서버에서는 컨트롤러 가드(`@Permissions('site.assign')`)가 그 자리를 막는다 |
 * | 동형 변경(`nextType === currentType`) 통과 | 서버는 조기 반환으로 걷어낸다 — 화면은 「현재 선택」으로 그린다 |
 * | 어댑터(`toDesignationAssignment`·`findCurrentPrimary`) | 목록 응답 형상 → 판정 입력 |
 *
 * ## 정본은 서버다 — 이것은 어포던스다
 *
 * 최종 판정은 `apps/server/src/sites/sites.service.ts` `updateAssignment`가 한다. 이 함수는 **버튼을 미리
 * 잠그고 사유를 말하기 위한 것**이고, 화면이 쥔 `assignments`가 낡았으면(다른 사람이 방금 PRIMARY를
 * 바꿨으면) 판정이 갈릴 수 있다. 갈릴 때의 경로는 서버 403 → `extractApiError` → 토스트로,
 * **서버가 쓴 사유 문구가 그대로 사용자에게 닿는다** — 그리고 이제 그 문구는 같은 shared 상수에서 온다.
 *
 * ## 전제 — `assignments`는 이미 활성 멤버십만이다
 *
 * 공석 판정의 정본 축은 「`assignmentType === 'PRIMARY'` 배정이 **활성 멤버십에** 하나도 없음」이다
 * (`apps/server/src/common/utils/site-primary-vacancy.util.ts` · `pages/sites/sitesView.ts`
 * `findPrimaryAssignee`). 이 파일은 그 필터를 **다시 걸지 않는다** — 목록 API
 * (`sites.service.getAssignments`)가 `where: { siteId, member: { leftAt: null } }`로 소스에서 이미 걸러
 * 내려주기 때문이다. 그 where가 사라지면 여기 판정이 조용히 틀려진다(떠난 사람의 배정이 「PRIMARY가
 * 있다」로 읽혀 공석 충원이 교체로 오판된다).
 */

/** 판정에 필요한 배정 행의 최소 형상 — 전체 `SiteAssignment`를 요구하지 않는다(spec 픽스처가 가벼워진다). */
export interface PrimaryDesignationAssignment {
  memberId: string;
  assignmentType?: AssignmentType;
}

/**
 * 거부 사유 식별자 — shared 사유 셋 **+ 화면 전용 하나**.
 *
 * 벽 셋(`SELF_PRIMARY_…`·`PRIMARY_REPLACE_…`·`PRIMARY_DEMOTE_…`)은 shared에서 온다(사본이 아니다).
 * 서버 에러 코드와 1:1이 아닌 이유는 그 화면 전용 축 하나뿐이다.
 */
export type PrimaryDesignationDenialReason =
  // ⚠️ 옛 `TARGET_IS_ORG_OWNER`(대상이 조직 소유자면 유형 변경 불가)는 **2026-09-26 사용자 결정으로
  //    이 축에서 걷혔다** — 서버 `updateAssignment`에 그 검사가 아예 없고, 화면만 잠근 탓에 **소유자가
  //    정 담당자인 현장을 UI로 공석화할 수 없었다**(그 소유자 본인조차). 이제 벽(②③)만 남는다:
  //    소유자를 강등하려면 소유자이거나 그 당사자 본인이어야 하고, 공석에 소유자를 앉히는 것은 키만으로
  //    된다(대상이 자기가 아니므로 상승이 없다). 그 축은 **할당 해제 버튼에만** 남아 있다
  //    (`AssigneeDetailModal`의 `canUnassign && !targetIsOrgOwner` — 서버가 `isOwnerMember`이고
  //    「소유자를 현장에서 해제할 수 있나」는 별개 질문이다). 두 버튼 조건을 「일관성」을 이유로 합치지 말 것.
  /** `site.assign` 자체가 없다 — 버튼 자리를 내지 않는다(아래 `canRenderAssignmentTypeChoice`). */
  | 'NO_ASSIGN_PERMISSION'
  | SharedDenialReason;

export type PrimaryDesignationVerdict =
  | { allowed: true }
  | { allowed: false; reason: PrimaryDesignationDenialReason; message: string };

/**
 * 사유 문구 — **다음 행동을 알려주는 문장**으로 쓴다. 「막혔다」만 말하면 사용자는 무엇을 해야 할지 모른다.
 *
 * 벽 셋은 shared 상수에서 **파생**한다(손으로 옮겨 적지 않는다 — 그 사본이 갈릴 수 있던 것이 종전 형상이고,
 * 그래서 문구 동치 spec을 짝으로 둬야 했다. 파생이면 그 spec 자체가 불필요해진다).
 *
 * `NO_ASSIGN_PERMISSION`만 **화면 전용**이다 — 서버에서는 컨트롤러 가드(`@Permissions('site.assign')`)가
 * 그 자리를 막으므로 shared 사유 union에 대응 항목이 없다. 권한이 없는 사용자에게도 **사유를 보여 준다**
 * (2026-09-27 사용자 결정 — 그 키는 `orgAdjustable`이라 막힘이 풀릴 수 있고, 사유가 있어야 「관리자에게
 * 요청한다」는 길로 이어진다. 그 계약은 `components/__tests__/assigneeTypeGate.test.tsx`가 잰다).
 */
export const PRIMARY_DESIGNATION_MESSAGES: Record<PrimaryDesignationDenialReason, string> = {
  NO_ASSIGN_PERMISSION: '담당자 유형을 변경할 권한이 없습니다.',
  ...(Object.fromEntries(
    Object.values(PRIMARY_DESIGNATION_DENIALS).map((denial) => [denial.reason, denial.message]),
  ) as Record<SharedDenialReason, string>),
};

export interface PrimaryDesignationInput {
  /** 누르려는 타입. */
  nextType: AssignmentType;
  /** 변경 대상 배정 행. */
  target: PrimaryDesignationAssignment;
  // ⚠️ 옛 `targetIsOrgOwner` 입력은 없다 — 위 `PrimaryDesignationDenialReason`의 주석 참조
  //    (2026-09-26 사용자 결정으로 이 축이 유형 변경에서 걷혔다. 해제 버튼에만 남는다).
  /** 그 현장의 배정 전량 — 현 PRIMARY를 여기서 찾는다(위 「전제」 참조). */
  assignments: PrimaryDesignationAssignment[];
  /**
   * 보는 사람의 **CompanyMember.id**. `SiteAssignment.memberId`와 같은 축이다(H-8a).
   * `undefined`(멤버십을 모름)면 「본인」 갈래가 성립하지 않아 ②③의 벽은 `isOwner` 하나만 남는다 —
   * 모르는 것을 본인으로 추정하면 벽이 뚫린다.
   */
  viewerMemberId: string | undefined;
  /** 소유권 성질(`usePermissions().isOwner`) — 조직이 RBAC로 줄 수 없는 축. */
  isOwner: boolean;
  /** `site.assign` — 이 행위의 권한 키. 벽과 **AND**다. */
  canAssign: boolean;
  /** `site.manage` — ①의 자기 승격에서만 쓴다. */
  canManageSite: boolean;
}

/**
 * 버튼 **자리 자체**를 낼지 — 권한 키가 없으면 버튼을 내지 않고 현재 값만 보여 준다.
 *
 * 벽(②③)은 **풀릴 수 있는** 조건이라(현 PRIMARY가 스스로 넘기거나 소유자가 한다) 숨기지 않고
 * **비활성 + 사유**로 낸다 — 숨기면 「기능이 없다」로 읽히고 다음 행동을 알려주지 못한다.
 *
 * ⚠️ 2026-09-26까지 여기 `!targetIsOrgOwner` 항이 함께 있었고, 그것이 **대상이 조직 소유자인 배정을
 * 통째로 잠갔다.** 서버엔 그 검사가 없어 화면만 좁았던 자리다 — 위 `PrimaryDesignationDenialReason`
 * 주석이 걷어낸 근거를 담고 있다. 인자를 하나만 받는 것이 이상해 보여도 **되돌리지 말 것**(축이
 * 하나인 것이 결론이다).
 */
export function canRenderAssignmentTypeChoice(input: { canAssign: boolean }): boolean {
  return input.canAssign;
}

/** 그 현장의 현 정 담당자 배정 행 — 없으면 `undefined`(= 공석). */
export function findCurrentPrimary(
  assignments: PrimaryDesignationAssignment[],
): PrimaryDesignationAssignment | undefined {
  return assignments.find((a) => a.assignmentType === 'PRIMARY');
}

/** `SiteAssignment`(목록 응답 형상)에서 판정 입력으로 줄이는 어댑터. */
export function toDesignationAssignment(
  assignment: Pick<SiteAssignment, 'memberId' | 'assignmentType'>,
): PrimaryDesignationAssignment {
  return { memberId: assignment.memberId, assignmentType: assignment.assignmentType };
}

/**
 * 화면 판정 — **화면 전용 축 둘을 앞에 두고, 벽은 shared에 맡긴다.**
 *
 * 호출부(데스크톱·모바일 공용 모달)가 이 하나를 쓰므로 레이아웃마다 답이 갈리지 않고, 벽 자체는 서버와
 * **같은 함수**라 층마다 갈리지도 않는다.
 */
export function resolvePrimaryDesignation(input: PrimaryDesignationInput): PrimaryDesignationVerdict {
  const { nextType, target, assignments, viewerMemberId, isOwner, canAssign, canManageSite } = input;

  const deny = (reason: PrimaryDesignationDenialReason): PrimaryDesignationVerdict => ({
    allowed: false,
    reason,
    message: PRIMARY_DESIGNATION_MESSAGES[reason],
  });

  // ⚠️ 여기 `if (targetIsOrgOwner) return deny(…)`가 있었다 — 2026-09-26 사용자 결정으로 걷혔다
  //    (근거는 위 `PrimaryDesignationDenialReason` 주석). 대상이 조직 소유자여도 벽(②③)만 판정한다.
  const currentType: AssignmentType = target.assignmentType ?? 'SECONDARY';
  // 동형 변경은 서버가 조기 반환으로 걷어낸다(`updateAssignment`) — 누를 일이 없는 자리라 잠그지 않는다.
  // (모달은 이 버튼을 「현재 선택」으로 그린다.) 벽에 넘기지 않는 이유도 같다 — 서버도 벽 앞에서 끊는다.
  if (nextType === currentType) return { allowed: true };

  if (!canAssign) return deny('NO_ASSIGN_PERMISSION');

  // 벽 — 판정은 shared 한 벌. 「본인」 갈래는 멤버십을 알 때만 성립한다(모르는 것을 본인으로 추정하지 않는다).
  const currentPrimary = findCurrentPrimary(assignments);
  const situation = classifyPrimaryDesignation({
    requestedType: nextType,
    // ③의 축은 **대상 행의 현재 타입**이다(요청 타입이 아니다 — shared 모듈 JSDoc ⚠️)
    targetCurrentType: currentType,
    targetIsRequester: viewerMemberId !== undefined && target.memberId === viewerMemberId,
    // 화면은 목록에서 찾는다(서버는 활성 멤버십 축 헬퍼로 조회한다) — 「없으면 공석」이 같은 뜻이 되는
    // 근거는 위 「전제」 절이다. `viewerMemberId`가 undefined면 `requesterMemberId`도 그것과 같을 수
    // 없어야 하므로 절대 일치하지 않는 값을 넘긴다(빈 문자열은 `memberId`로 쓰이지 않는다).
    currentPrimaryMemberId: currentPrimary?.memberId ?? null,
    requesterMemberId: viewerMemberId ?? '',
  });
  const denial = judgePrimaryDesignation(situation, { isOwner, hasSiteManage: canManageSite });
  return denial ? deny(denial.reason) : { allowed: true };
}
